import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "clsx";
import { toast } from "sonner";
import {
  extrairDadosArquivo,
  isPdfOrXmlFile,
  isPdfFile,
  isXmlFile,
} from "../../../utils/pdfExtraction";

const STATUS = {
  PENDING: "pendente",
  READING: "lendo",
  READY: "pronto",
  LOADED: "lido",
  ERROR: "erro",
};

const buildEntry = (file, overrides = {}) => {
  const nome = overrides.nome ?? file?.name ?? "Entrada manual";
  const tamanho = overrides.tamanho ?? file?.size ?? 0;

  return {
    id:
      overrides.id ??
      `${nome}-${tamanho}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    file: file ?? null,
    nome,
    tamanho,
    status: overrides.status ?? STATUS.PENDING,
    mensagem: overrides.mensagem ?? "",
    erro: overrides.erro ?? null,
    nfe: overrides.nfe ?? "",
    chassi: overrides.chassi ?? "",
    ve101_key_public: overrides.ve101_key_public ?? "",
    rawText: overrides.rawText ?? "",
    manual: overrides.manual ?? false,
  };
};

const toTrimmedUpper = (value) => (value || "").replace(/\s+/g, "").toUpperCase();
const toTrimmed = (value) => (value || "").trim();
const getEntryDataState = (entry) => {
  const nfe = toTrimmed(entry?.nfe);
  const chassi = toTrimmedUpper(entry?.chassi);
  const hasNfe = Boolean(nfe);
  const hasChassi = Boolean(chassi);
  return {
    hasNfe,
    hasChassi,
    isComplete: hasNfe && hasChassi,
    isEmpty: !hasNfe && !hasChassi,
  };
};
const hasRequiredData = (entry) => getEntryDataState(entry).isComplete;
const getFileKindLabel = (file) => {
  if (isPdfFile(file)) {
    return "PDF";
  }
  if (isXmlFile(file)) {
    return "XML";
  }
  return "arquivo";
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** index;
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const statusLabel = {
  [STATUS.PENDING]: "Pendente",
  [STATUS.READING]: "Processando",
  [STATUS.LOADED]: "Lido",
  [STATUS.READY]: "Pronto",
  [STATUS.ERROR]: "Erro",
};

const statusClass = {
  [STATUS.PENDING]: "neutral",
  [STATUS.READING]: "neutral",
  [STATUS.LOADED]: "info",
  [STATUS.READY]: "success",
  [STATUS.ERROR]: "danger",
};

const FileRow = ({
  entry,
  onUpdate,
  onRemove,
}) => {
  const onFieldChange = (field) => (event) => {
    const rawValue = event.target.value;
    const value = field === "chassi" ? toTrimmedUpper(rawValue) : toTrimmed(rawValue);
    const next = { ...entry, [field]: value };
    if (field === "nfe" || field === "chassi") {
      const currentNfe = field === "nfe" ? value : entry.nfe;
      const currentChassi = field === "chassi" ? value : entry.chassi;
      const hasEnoughData = Boolean(
        toTrimmed(currentNfe) && toTrimmedUpper(currentChassi),
      );
      next.ve101_key_public = "";
      next.status = hasEnoughData ? STATUS.READY : STATUS.LOADED;
      next.mensagem = hasEnoughData
        ? "Dados prontos para envio. Revise antes de confirmar."
        : "Informe todos os dados necessários.";
    }
    onUpdate(next);
  };

  return (
    <div className="lote-file-row">
      <div className="lote-file-row__header">
        <div>
          <strong>{entry.nome}</strong>
          <span className="lote-file-row__meta">{formatBytes(entry.tamanho)}</span>
        </div>
        <div className="lote-file-row__status">
          <span className={classNames("status-pill", statusClass[entry.status] || "neutral")}>
            {statusLabel[entry.status] || entry.status}
          </span>
        </div>
      </div>
      <div className="lote-file-row__fields">
        <div className="field">
          <label htmlFor={`nfe-${entry.id}`}>Chave NFe</label>
          <input
            id={`nfe-${entry.id}`}
            value={entry.nfe}
            onChange={onFieldChange("nfe")}
            placeholder="Informe a chave"
          />
        </div>
        <div className="field">
          <label htmlFor={`chassi-${entry.id}`}>Chassi</label>
          <input
            id={`chassi-${entry.id}`}
            value={entry.chassi}
            onChange={onFieldChange("chassi")}
            placeholder="Informe o chassi"
          />
        </div>
      </div>
      {!!entry.mensagem && entry.status !== STATUS.ERROR && (
        <div className="lote-file-row__message">{entry.mensagem}</div>
      )}
      {!!entry.erro && (
        <div className="lote-file-row__error">
          {entry.erro}
        </div>
      )}
      <div className="lote-file-row__footer">
        <button
          type="button"
          className="btn-link danger"
          onClick={() => onRemove(entry.id)}
        >
          Remover
        </button>
      </div>
    </div>
  );
};

function LoteUploadModal({
  aberto,
  onClose,
  onConfirm,
  enviando = false,
}) {
  const [entries, setEntries] = useState([]);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const manualCounterRef = useRef(0);
  const [dragActive, setDragActive] = useState(false);
  const duplicateToastRef = useRef({ keys: new Set(), timer: null });

  useEffect(() => {
    if (!aberto) {
      setEntries([]);
      dragCounterRef.current = 0;
      setDragActive(false);
      manualCounterRef.current = 0;
      if (duplicateToastRef.current.timer) {
        clearTimeout(duplicateToastRef.current.timer);
        duplicateToastRef.current.timer = null;
      }
      duplicateToastRef.current.keys = new Set();
    }
  }, [aberto]);

  const queueDuplicateToast = useCallback((key) => {
    const store = duplicateToastRef.current;
    if (key) {
      if (store.keys.has(key)) {
        return;
      }
      store.keys.add(key);
    }
    if (store.timer) {
      return;
    }
    store.timer = setTimeout(() => {
      const quantidade = store.keys.size;
      if (quantidade > 0) {
        toast.warning(
          quantidade === 1
            ? "1 item duplicado foi ignorado."
            : `${quantidade} itens duplicados foram ignorados.`,
        );
      }
      store.keys = new Set();
      store.timer = null;
    }, 250);
  }, []);

  const atualizarEntry = useCallback(
    (next) => {
      setEntries((prev) =>
        prev.map((item) => (item.id === next.id ? { ...item, ...next } : item)),
      );
    },
    [],
  );

  const removerEntry = useCallback((id) => {
    setEntries((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const processarEntry = useCallback(
    async (entry) => {
      const arquivoLabel = getFileKindLabel(entry.file);
      atualizarEntry({
        ...entry,
        status: STATUS.READING,
        mensagem: `Lendo ${arquivoLabel}...`,
      });
      const resultado = await extrairDadosArquivo(entry.file);
      if (!resultado.success) {
        atualizarEntry({
          ...entry,
          status: STATUS.ERROR,
          erro: resultado.error || "Não foi possível extrair dados deste arquivo.",
          mensagem: "Revise o PDF ou preencha manualmente.",
        });
        return;
      }

      const nfe = toTrimmed(resultado.nfe || "");
      const chassi = toTrimmedUpper(resultado.chassis || "");
      const hasEnoughData = Boolean(nfe && chassi);
      if (!hasEnoughData) {
        const camposFaltantes = [];
        if (!nfe) {
          camposFaltantes.push("a chave NFe");
        }
        if (!chassi) {
          camposFaltantes.push("o chassi");
        }
        toast.error(
          `Não foi possível adicionar "${entry.nome}". Verifique se o PDF contém ${camposFaltantes.join(" e ")}.`,
        );
        setEntries((prev) => prev.filter((item) => item.id !== entry.id));
        return;
      }
      setEntries((prev) => {
        const duplicateKey = `${nfe || "sem-nfe"}::${chassi || "sem-chassi"}`;
        const duplicado = prev.some(
          (item) =>
            item.id !== entry.id &&
            toTrimmed(item.nfe) === nfe &&
            toTrimmedUpper(item.chassi) === chassi,
        );
        if (duplicado) {
          queueDuplicateToast(duplicateKey);
          return prev.filter((item) => item.id !== entry.id);
        }

        return prev.map((item) => {
          if (item.id !== entry.id) {
            return item;
          }
          return {
            ...item,
            status: STATUS.READY,
            erro: null,
            mensagem: "Dados extraídos. Revise antes de confirmar.",
            nfe,
            chassi,
            ve101_key_public: "",
            rawText: resultado.rawText,
          };
        });
      });
    },
    [atualizarEntry, queueDuplicateToast],
  );

  const adicionarArquivos = useCallback(
    (fileList) => {
      const arquivos = Array.from(fileList);
      const validos = arquivos.filter((file) => isPdfOrXmlFile(file));
      const ignorados = arquivos.length - validos.length;

      if (!validos.length) {
        if (ignorados > 0) {
          toast.error("Apenas arquivos PDF ou XML são suportados no envio em lote.");
        }
        return;
      }

      if (ignorados > 0) {
        toast.warning(
          ignorados === 1
            ? "1 arquivo foi ignorado (formato não suportado)."
            : `${ignorados} arquivos foram ignorados (formato não suportado).`,
        );
      }

      const novos = validos.map((file) => buildEntry(file));
      setEntries((prev) => [...prev, ...novos]);
      novos.forEach((entry) => {
        processarEntry(entry);
      });
    },
    [processarEntry],
  );

  const adicionarEntradaManual = useCallback(() => {
    manualCounterRef.current += 1;
    const entry = buildEntry(null, {
      nome: `Entrada manual ${manualCounterRef.current}`,
      status: STATUS.LOADED,
      mensagem: "Digite a chave NFe e o chassi.",
      manual: true,
    });
    setEntries((prev) => [entry, ...prev]);
  }, [setEntries]);

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer?.files?.length) {
      adicionarArquivos(event.dataTransfer.files);
    }
    dragCounterRef.current = 0;
    setDragActive(false);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    if (event.dataTransfer?.types?.includes("Files")) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setDragActive(false);
    }
  };

  const selecionarArquivos = () => {
    fileInputRef.current?.click();
  };

  const focusEntryField = useCallback((entryId, field) => {
    if (typeof document === "undefined") {
      return;
    }
    const element = document.getElementById(`${field}-${entryId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof element.focus === "function") {
        element.focus();
      }
    }
  }, []);

  const onConfirmar = useCallback(() => {
    const parcial = entries.find((entry) => {
      const estado = getEntryDataState(entry);
      return (estado.hasNfe || estado.hasChassi) && !estado.isComplete;
    });

    if (parcial) {
      toast.error("Complete a chave NFe e o chassi antes de enviar o lote.");
      const estado = getEntryDataState(parcial);
      const missingField = estado.hasNfe ? "chassi" : "nfe";
      focusEntryField(parcial.id, missingField);
      return;
    }

    const completos = entries.filter((entry) => hasRequiredData(entry));
    if (!completos.length) {
      toast.error("Informe a chave NFe e o chassi para ao menos um item antes de enviar o lote.");
      return;
    }

    onConfirm(completos);
  }, [entries, focusEntryField, onConfirm]);

  const arquivosProntos = useMemo(
    () => entries.filter((entry) => hasRequiredData(entry)),
    [entries],
  );

  if (!aberto) {
    return null;
  }

  return (
    <div className="lote-modal-backdrop">
      <div className="lote-modal">
        <div className="lote-modal__header">
          <h2>Envio em Lote</h2>
          <button type="button" className="btn-icon" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div
          className={classNames(
            "lote-modal__dropzone",
            dragActive && "lote-modal__dropzone--dragover",
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        >
          <span
            className={classNames(
              "material-symbols-outlined",
              dragActive ? "lote-modal__dropzone-icon--active" : "lote-modal__dropzone-icon",
            )}
          >
            {dragActive ? "download" : "cloud_upload"}
          </span>
          <p>
            {dragActive
              ? "Solte os arquivos para adicionar ao lote"
              : `Você pode adicionar arquivos XML e PDFs, mas caso não seja possível use a opção "Adicionar manualmente".`}
          </p>
          {!dragActive && (
            <div
              className="lote-modal__dropzone-actions"
              style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}
            >
              <button type="button" className="btn btn-primary" onClick={selecionarArquivos}>
                Selecionar arquivos
              </button>
              <button type="button" className="btn btn-secondary" onClick={adicionarEntradaManual}>
                Adicionar manualmente
              </button>
            </div>
          )}
          {dragActive && (
            <p className="lote-modal__dropzone-status">
              Os arquivos serão lidos automaticamente assim que forem soltos.
            </p>
          )}
          <p className="lote-modal__hint">
            Após a leitura revise a chave NFe e o chassi (obrigatórios). Vincular um registro existente é opcional.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,application/xml,text/xml,.pdf,.xml"
            multiple
            hidden
            onChange={(event) => {
              if (event.target.files?.length) {
                adicionarArquivos(event.target.files);
                event.target.value = "";
              }
            }}
          />
        </div>
        <div className="lote-modal__body">
          {entries.length === 0 && (
            <div className="lote-modal__empty">
              Sem arquivos. Arraste PDFs/XMLs, clique em "Selecionar arquivos" ou adicione manualmente.
            </div>
          )}
          {entries.length > 0 && (
            <div className="lote-modal__list">
              {entries.map((entry) => (
                <FileRow
                  key={entry.id}
                  entry={entry}
                  onUpdate={atualizarEntry}
                  onRemove={removerEntry}
                />
              ))}
            </div>
          )}
        </div>
        <div className="lote-modal__footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!arquivosProntos.length || enviando}
            onClick={onConfirmar}
          >
            {enviando ? "Enviando..." : `Enviar lote (${arquivosProntos.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoteUploadModal;
